/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import {
  FLink,
  Button,
  RadioButton,
  CurrencyTextInput,
  Tooltip,
  LoadingIndicator,
  ComboBox,
  Icons,
} from '@components';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { currency } from '@utils';
import { format } from 'date-fns';
import {
  mdiBookMultipleOutline,
  mdiBookOpenBlankVariant,
  mdiCalendar,
  mdiChatQuestionOutline,
  mdiCommentAlertOutline,
  mdiFileDownloadOutline,
  mdiLightbulbOutline,
  mdiScriptTextOutline,
} from '@mdi/js';
import {
  obterContratosRequest,
  obterDadosSolicitacaoRequest,
  setSimulation,
} from '../../../../store/modules/emprestimo/actions';
import {
  REGRAS_GERAIS_EMPRESTIMO,
  REGULAMENTO_EMPRESTIMO,
  SOLICITACAO_ABSOLUTE_PATH,
  TIRA_DUVIDAS,
  EMPRESTIMO_CONSCIENTE,
  IMPOSTO_DE_RENDA,
  CALENDARIO_EMPRESTIMO,
  CONTRATOS_ABSOLUTE_PATH,
} from '../../../../common/utils/paths';
import { EmprestimoService } from '../../../../common/services/emprestimo';

const Simulador = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.loading.show);
  const [firstLoad, setFirstLoad] = useState(true);

  const {
    total: t,
    quantidadeMaximaParcelas,
    saldoDevedor: valorTotalSaldoDevedor,
    requestDone: requestDoneSaldoDevedor,
    limiteMinimo,
  } = useSelector((state) => state.emprestimo.limite);

  const {
    dataPrimeiraParcela,
    requestDone,
    parcelaMinCalculada,
    valorLiberado,
    matrizOpcoesParcelas,
    dataPrevistaCredito,
  } = useSelector((state) => state.emprestimo.solicitacao);

  const total = Math.floor(t);
  const intervaloDeValoresValidos = t - limiteMinimo;
  const vintePorCentoDoTotal = Math.floor(
    intervaloDeValoresValidos * 0.2 + limiteMinimo
  );
  const quarentaPorCentoDoTotal = Math.floor(
    intervaloDeValoresValidos * 0.4 + limiteMinimo
  );
  const sessentaPorCentoDoTotal = Math.floor(
    intervaloDeValoresValidos * 0.6 + limiteMinimo
  );
  const oitentaPorCentoDoTotal = Math.floor(
    intervaloDeValoresValidos * 0.8 + limiteMinimo
  );

  const [valorEmprestimo, setValorEmprestimo] = useState(total);
  const [emprestimoCustom, setEmprestimoCustom] = useState(0);
  const [parcelasCustom, setParcelasCustom] = useState(0);
  const isCustom = 'outro';

  useEffect(() => {
    if (total && quantidadeMaximaParcelas) {
      if (
        valorEmprestimo === isCustom &&
        emprestimoCustom >= Math.ceil(limiteMinimo) &&
        emprestimoCustom <= total
      ) {
        dispatch(
          setSimulation({
            valor: emprestimoCustom,
            parcelas: Number(parcelasCustom) || quantidadeMaximaParcelas,
          })
        );
      } else if (valorEmprestimo !== isCustom) {
        dispatch(
          setSimulation({
            valor: valorEmprestimo || total,
            parcelas: Number(parcelasCustom) || quantidadeMaximaParcelas,
          })
        );
      }
    }
  }, [
    dispatch,
    emprestimoCustom,
    parcelasCustom,
    valorEmprestimo,
    total,
    quantidadeMaximaParcelas,
    limiteMinimo,
  ]);

  useEffect(() => {
    dispatch(obterDadosSolicitacaoRequest());
  }, [
    dispatch,
    emprestimoCustom,
    valorEmprestimo,
    total,
    quantidadeMaximaParcelas,
    limiteMinimo,
  ]);

  useEffect(() => {
    dispatch(obterContratosRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!valorEmprestimo && total) {
      setValorEmprestimo(total);
    }
  }, [total, valorEmprestimo]);

  useEffect(() => {
    if (!isLoading && firstLoad && requestDoneSaldoDevedor && requestDone) {
      setFirstLoad(false);
    }
  }, [firstLoad, isLoading, requestDoneSaldoDevedor, requestDone]);

  return isLoading && firstLoad ? (
    <LoadingIndicator />
  ) : (
    <div className="card h-100">
      <div className="mt-3 px-45">
        <p className="text-muted ts-14">
          Preencha os campos abaixo para realizar as simulações para contratação
          de empréstimo. Simule quantas vezes quiser antes de seguir para
          assinatura do contrato.{' '}
        </p>
      </div>
      <div className="card-body">
        <div className="d-flex justify-content-between gap-4 flex-column flex-lg-row">
          <div className="d-flex flex-column border-container align-items-center">
            <span>
              Saldo Devedor Atual:
              <Tooltip title="Somatório dos saldos de contratos de empréstimos contratados anteriormente e calculados até dia 30 do mês corrente">
                ?
              </Tooltip>
            </span>
            <span className="fs-4 mt-3">
              {valorTotalSaldoDevedor ? currency(valorTotalSaldoDevedor) : '-'}
            </span>
          </div>
          <div className="d-flex flex-column border-container align-items-center">
            <span>
              Limite de Empréstimo:
              <Tooltip title="O valor máximo do empréstimo é obtido pela fórmula presente em regulamento">
                ?
              </Tooltip>
            </span>
            <span className="fs-4 mt-3">{currency(total)}</span>
          </div>
        </div>
        <div className="border-container my-3">
          <div className="d-flex justify-content-md-between align-items-center ">
            <span>Seu crédito pré-aprovado:</span>
            <strong className="fs-4">
              {valorEmprestimo === isCustom
                ? currency(emprestimoCustom)
                : currency(valorEmprestimo)}
            </strong>
          </div>
          <div
            className="d-flex gap-1 my-2 flex-wrap"
            onChange={(e) => setValorEmprestimo(e.target.value)}
          >
            <RadioButton
              name="valores"
              value={vintePorCentoDoTotal}
              id="radio1"
              label={currency(vintePorCentoDoTotal)}
              disabled={isLoading}
            />
            <RadioButton
              name="valores"
              value={quarentaPorCentoDoTotal}
              id="radio2"
              label={currency(quarentaPorCentoDoTotal)}
              disabled={isLoading}
            />
            <RadioButton
              name="valores"
              value={sessentaPorCentoDoTotal}
              id="radio3"
              label={currency(sessentaPorCentoDoTotal)}
              disabled={isLoading}
            />
            <RadioButton
              name="valores"
              value={oitentaPorCentoDoTotal}
              id="radio4"
              label={currency(oitentaPorCentoDoTotal)}
              disabled={isLoading}
            />
            <RadioButton
              defaultChecked
              name="valores"
              value={total}
              id="radio5"
              label={currency(total)}
              disabled={isLoading}
            />
            <RadioButton
              name="valores"
              value="outro"
              id="radio6"
              label="OUTRO"
              disabled={isLoading}
            />
          </div>
          <CurrencyTextInput
            className="mb-8px"
            disabled={valorEmprestimo !== isCustom}
            height={38}
            fontSize="ts-16 border-50"
            value={emprestimoCustom}
            min={Math.ceil(limiteMinimo)}
            max={total}
            onChange={(val) => {
              setEmprestimoCustom(val);
            }}
          />
          <p>
            Escolha de {currency(Math.ceil(limiteMinimo))} até {currency(total)}
          </p>
        </div>
        <div className="border-container my-3">
          <div className="d-flex justify-content-md-between align-items-center ">
            <span>Selecione a quantidade de parcelas:</span>
            <strong className="fs-4">
              {Number(parcelasCustom) || quantidadeMaximaParcelas} meses
            </strong>
          </div>
          <div className="col-lg-4">
            <ComboBox
              className="mb-3"
              disabled={isLoading}
              options={matrizOpcoesParcelas.map((item) => {
                return {
                  label: `${item.PRAZO[0]} parcelas - ${currency(
                    parseFloat(item.VLR_PARC[0])
                  )}`,
                  value: parseInt(item.PRAZO[0], 10),
                };
              })}
              value={parcelasCustom}
              defaultValue={quantidadeMaximaParcelas}
              onChange={setParcelasCustom}
            />
          </div>
          {isLoading ? (
            <p>Carregando...</p>
          ) : (
            <p>
              {parcelaMinCalculada !== Number(quantidadeMaximaParcelas)
                ? `Escolha entre ${parcelaMinCalculada} e ${quantidadeMaximaParcelas}`
                : ''}
            </p>
          )}
        </div>
        <div className="d-flex justify-content-between gap-4 flex-column flex-lg-row">
          <div className="border-container my-3">
            <div className="d-flex justify-content-md-between mb-2">
              <p>Valor aproximado das parcelas:</p>
              <strong className="fs-4">
                {isLoading
                  ? 'Carregando...'
                  : currency(
                      matrizOpcoesParcelas.find(
                        (obj) => parseInt(obj.PRAZO[0], 10) === parcelasCustom
                      )?.VLR_PARC[0]
                    ) || '-'}
              </strong>
            </div>
            <div className="d-flex justify-content-md-between mb-2">
              <p>Previsão de crédito</p>

              <strong>
                {isLoading
                  ? 'Carregando...'
                  : format(
                      dataPrevistaCredito
                        ? new Date(`${dataPrevistaCredito} `)
                        : new Date(),
                      'dd/MM/yyyy'
                    )}
              </strong>
            </div>
            <div className="d-flex justify-content-md-between">
              <p>Primeiro vencimento:</p>

              <strong>
                {isLoading
                  ? 'Carregando...'
                  : format(
                      dataPrimeiraParcela
                        ? new Date(`${dataPrimeiraParcela} `)
                        : new Date(),
                      'dd/MM/yyyy'
                    )}
              </strong>
            </div>
          </div>
          <div className="border-container my-3">
            <div className="d-flex justify-content-md-between mb-2">
              <p>
                Valor previsto para liberação:
                <Tooltip title="Valor líquido previsto">?</Tooltip>
              </p>
            </div>
            <strong className="fs-4 mt-3">
              {isLoading ? 'Carregando...' : currency(valorLiberado)}
            </strong>
          </div>
        </div>

        <Button
          className="text-uppercase"
          onClick={() => {
            // dispatch()
            navigate(SOLICITACAO_ABSOLUTE_PATH);
          }}
          disabled={
            isLoading ||
            parcelasCustom > quantidadeMaximaParcelas ||
            parcelasCustom < parcelaMinCalculada ||
            (valorEmprestimo === isCustom &&
              (emprestimoCustom < Math.ceil(limiteMinimo) ||
                emprestimoCustom > total))
          }
        >
          Veja o resultado da sua simulação
        </Button>
      </div>
    </div>
  );
};

const TermosSimular = ({ setAceiteTermos }) => {
  const { novoContratoBloqueado, requestDone, msgErro, msgBloqueio } =
    useSelector((state) => state.emprestimo.limite);
  const { matricula } = useSelector((state) => state.user);

  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    const service = new EmprestimoService();
    service.validaEmprestimoManual(matricula).then((response) => {
      if (response.AcessoAntigo === 'Sim') {
        setManualMode(true);
      }
    });
  }, [matricula]);

  return !requestDone ? (
    <LoadingIndicator />
  ) : (
    <div className="card h-100">
      {msgBloqueio && (
        <div className="card-body d-flex gap-3 align-items-center">
          <Icons
            path={mdiCommentAlertOutline}
            color="var(--primary-color)"
            size={3}
          />
          <div className="card-body flex-column gap-2">
            <h3 className="fw-bold">Atenção</h3>
            <h3>{msgBloqueio}</h3>
          </div>
        </div>
      )}

      {msgErro && (
        <div className="card-body d-flex gap-3 align-items-center">
          <Icons
            path={mdiCommentAlertOutline}
            color="var(--primary-color)"
            size={3}
          />
          <div className="card-body flex-column gap-2">
            <h3 className="fw-bold">Simulação indisponível</h3>
            <h3>{msgErro}</h3>
          </div>
        </div>
      )}

      {!msgBloqueio && !msgErro && (
        <div className="card-body d-flex flex-column gap-3">
          <h3 className="fw-bold">Atenção</h3>

          {manualMode ? (
            <>
              <p>Prezado participante,</p>

              <p>
                Na próxima página você poderá realizar simulações para
                contratação de empréstimo.{' '}
                <b>
                  Recomendamos que faça pelo APP da Forluz, no seu celular, para
                  uma melhor experiência.
                </b>{' '}
                Seu limite para solicitação é definido pelas regras do
                Regulamento dos Empréstimos Forluz, disponível abaixo.
              </p>

              <p>
                O pedido de empréstimo passa por análise interna de aprovação, e
                sua confirmação ocorre em até 1 dia antes da data do crédito.
              </p>

              <p>Passos para realizar sua solicitação:</p>

              <ul className="fw-bold">
                <li>1- Simular o empréstimo</li>
                <li>2- Imprimir o contrato e o regulamento</li>
                <li>
                  3- Assinar de próprio punho os cinco campos de assinatura do
                  financiado (duas páginas do contrato e três páginas do
                  regulamento)
                </li>
                <li>
                  4- As assinaturas de todos os campos devem ser idênticas ao
                  documento de identificação{' '}
                </li>
                <li>
                  5- Digitalizar (em PDF) ou tirar foto das páginas (contrato e
                  regulamento), garantindo que estejam legíveis, sem cortes e
                  rasuras
                </li>
                <li>
                  6- Enviar os documentos pelo botão "Requerimento de
                  Empréstimo" disponível no menu "Requerimentos" ao lado ou na
                  ultima tela da simulação.
                </li>
              </ul>
              <p>
                Os créditos ocorrem nos dias 15 (ou dia útil posterior) e 30 (ou
                em dia útil anterior).
              </p>
              <p>
                <b>Importante:</b> não assuma compromissos financeiros sem a
                confirmação do crédito em sua conta.
              </p>
            </>
          ) : (
            <>
              <p>
                <p>
                  Na próxima página você poderá realizar simulações para
                  contratação de empréstimo.{' '}
                  <b>
                    Recomendamos que faça pelo APP da Forluz, no seu celular,
                    para uma melhor experiência.
                  </b>{' '}
                  Seu limite para solicitação é definido pelas regras do
                  Regulamento dos Empréstimos Forluz, disponível abaixo.
                </p>
              </p>

              <p>
                O pedido de empréstimo passa por análise interna de aprovação, e
                sua confirmação ocorre em até 1 dia útil antes da data do
                crédito.
              </p>

              <p>Passos para realizar sua solicitação:</p>

              <ul className="fw-bold">
                <li>1 - Simular o empréstimo;</li>
                <li>
                  2 - Assinar eletronicamente pela plataforma Clicksign
                  (seguindo as validações de segurança);
                </li>
                <li>
                  3 - Acompanhar o status do requerimento até o efetivo crédito
                  em conta.
                </li>
              </ul>

              <p>
                <b>
                  Para assinatura será necessário portar documento com foto (RG
                  ou CNH).
                </b>
              </p>

              <p>
                Os créditos ocorrem semanalmente de acordo com o{' '}
                <FLink className="d-inline fs-6" to={CALENDARIO_EMPRESTIMO}>
                  Calendário de Empréstimos.
                </FLink>
              </p>
              <p>
                <b>
                  Ao prosseguir, autorizo a coleta e utilização dos meus dados
                  pessoais e biometria facial pela Forluz, exclusivamente para
                  minha identificação e segurança. Entendo que meus dados serão
                  tratados de maneira confidencial e utilizados apenas para a
                  finalidade especificada.
                </b>
              </p>
              <p>
                <b>Importante:</b>
                <ul>
                  <li>
                    1 - Não assuma compromissos financeiros sem a confirmação do
                    crédito em sua conta.
                  </li>
                  <li>
                    2 - O crédito ocorre em conta corrente especifica, portanto
                    em caso de dúvidas em relação a conta cadastrada entre em
                    contato com o atendimento Forluz.
                  </li>
                  <li>
                    3 - Em caso de refinanciamento e havendo desconto da parcela
                    do mês referente ao contrato anterior, o valor recebido será
                    antecipado no novo contrato.
                  </li>
                </ul>
              </p>
            </>
          )}

          {novoContratoBloqueado && (
            <div className="background-primary ts-16 mb-3 p-4">
              <h4 className="fw-bold mb-2">Atenção</h4>
              {manualMode ? (
                <p>
                  Você possui um contrato de empréstimo que está em período de
                  carência ou há inadimplência. Portanto:
                  <ul>
                    <li>
                      Para solicitar um novo empréstimo, você deverá realizar o
                      pagamento do boleto para Antecipação de Carência ou
                      regularizar o pagamento das Parcelas em Aberto{' '}
                      <Link className="SignText" to={CONTRATOS_ABSOLUTE_PATH}>
                        (link)
                      </Link>
                      . A concessão do novo crédito estará condicionada ao
                      pagamento do boleto.
                    </li>
                    <li>
                      Os limites exibidos a seguir são apenas informativos. Em
                      função do pagamento do boleto, o valor líquido simulado
                      será alterado, tendo em vista a alteração no saldo devedor
                      do(s) contrato(s) anterior(es).
                    </li>
                  </ul>
                </p>
              ) : (
                <p>
                  Você possui um contrato de empréstimo que está em período de
                  carência ou há inadimplência. Portanto:
                  <ul>
                    <li>
                      Para solicitar um novo empréstimo, você deverá realizar o
                      pagamento do boleto para Antecipação de Carência ou
                      regularizar o pagamento das Parcelas em Aberto{' '}
                      <Link className="SignText" to={CONTRATOS_ABSOLUTE_PATH}>
                        (link)
                      </Link>
                      . A concessão do novo crédito estará condicionada ao
                      pagamento do boleto.
                    </li>
                    <li>
                      Os limites exibidos a seguir são apenas informativos. Em
                      função do pagamento do boleto, o valor líquido simulado
                      será alterado, tendo em vista a alteração no saldo devedor
                      do(s) contrato(s) anterior(es)."
                    </li>
                  </ul>
                </p>
              )}
            </div>
          )}

          <Button
            className="text-uppercase"
            onClick={() => {
              setAceiteTermos(true);
            }}
          >
            Li os termos e aceito
          </Button>
        </div>
      )}
    </div>
  );
};

export const Simular = () => {
  const service = new EmprestimoService();
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [file, setFile] = useState('');
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    service
      .obterURLPDFRegulamentoEmprestimo()
      .then((res) => {
        setFile(res);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      {aceiteTermos ? (
        <Simulador />
      ) : (
        <TermosSimular setAceiteTermos={setAceiteTermos} />
      )}

      <div className="row mt-4">
        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <div className="card h-100">
            <div className="card-body">
              <Icons
                path={mdiBookMultipleOutline}
                style={{ marginBottom: 20 }}
                size={2}
              />
            </div>

            <div className="card-footer mt-0">
              <FLink to={REGULAMENTO_EMPRESTIMO}>
                <div className="row">
                  <h1 className="ts-20 fw-bold">Regulamento</h1>
                  <p className="mt-2 mb-4 text-muted ts-14">
                    Antes de solicitar o empréstimo, é importante você conhecer
                    o regulamento para se informar sobre suas condições e
                    exigências. Você pode visualizar ou baixar o documento.
                  </p>
                </div>
              </FLink>
              <hr />
              {isLoading ? (
                'Carregando PDF...'
              ) : (
                <a
                  href={file}
                  rel="noreferrer"
                  target="_blank"
                  download
                  title="Clique para baixar o regulamento"
                  className="d-md-flex ts-14 mb-2 mt-4 align-items-center justify-content-between text-decoration-none fw-bold primary-color"
                >
                  Baixar
                  <Icons
                    path={mdiFileDownloadOutline}
                    color="var(--primary-color)"
                  />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <FLink to={EMPRESTIMO_CONSCIENTE} className="h-100">
            <div className="card h-100">
              <div className="card-body">
                <Icons
                  path={mdiLightbulbOutline}
                  style={{ marginBottom: 20 }}
                  size={2}
                />
              </div>
              <div className="card-footer mt-0">
                <h1 className="ts-20 fw-bold">Crédito consciente</h1>
                <p className="mt-3 text-muted ts-14">
                  Analise com serenidade as consequências antes de se decidir a
                  tomar um empréstimo e se endividar.
                </p>
              </div>
            </div>
          </FLink>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <FLink to={IMPOSTO_DE_RENDA} className="h-100">
            <div className="card h-100">
              <div className="card-body">
                <Icons
                  path={mdiScriptTextOutline}
                  style={{ marginBottom: 20 }}
                  size={2}
                />
              </div>
              <div className="card-footer mt-0">
                <h1 className="ts-20 fw-bold">Imposto de renda</h1>
                <p className="mt-3 text-muted ts-14">
                  Veja os valores referentes ao empréstimo relacionados ao
                  Imposto de Renda
                </p>
              </div>
            </div>
          </FLink>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <FLink to={TIRA_DUVIDAS} className="h-100">
            <div className="card h-100">
              <div className="card-body">
                <Icons
                  path={mdiChatQuestionOutline}
                  style={{ marginBottom: 20 }}
                  size={2}
                />
              </div>
              <div className="card-footer mt-0">
                <h1 className="ts-20 fw-bold">Tire Suas Dúvidas</h1>
                <p className="mt-3 text-muted ts-14">
                  Esclareça as suas dúvidas sobre a área de empréstimos e
                  compreenda melhor sobre a solicitação e simulação, a diferença
                  entre amortização e antecipação e outros tópicos.
                </p>
              </div>
            </div>
          </FLink>
        </div>

        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <FLink to={REGRAS_GERAIS_EMPRESTIMO} className="h-100">
            <div className="card h-100">
              <div className="card-body">
                <Icons
                  path={mdiBookOpenBlankVariant}
                  style={{ marginBottom: 20 }}
                  size={2}
                />
              </div>
              <div className="card-footer mt-0">
                <h1 className="ts-20 fw-bold">Regras gerais</h1>
                <p className="mt-3 text-muted ts-14">
                  Leia com atenção todas as cláusulas do contrato e o
                  Regulamento do Empréstimo Forluz, antes de fazer sua
                  solicitação. E não assuma nenhum compromisso sem confirmar
                  antecipadamente a liberação de crédito.
                </p>
              </div>
            </div>
          </FLink>
        </div>

        <div className="col-12 col-md-6 col-lg-4 mb-4 emprestimos-card">
          <FLink to={CALENDARIO_EMPRESTIMO} className="h-100">
            <div className="card h-100">
              <div className="card-body">
                <Icons
                  path={mdiCalendar}
                  style={{ marginBottom: 20 }}
                  size={2}
                />
              </div>
              <div className="card-footer mt-0">
                <h1 className="ts-20 fw-bold">Calendário de empréstimos</h1>
                <p className="mt-3 text-muted ts-14">
                  Acompanhe os prazos de solicitações, créditos e pagamentos de
                  empréstimos
                </p>
              </div>
            </div>
          </FLink>
        </div>
      </div>
    </>
  );
};
